<template lang="html">
  <section :id="id">
    <base-modal
      ref="baseModal"
      :maxHeight="modalHeight"
      :maxWidth="modalWidth"
      @on-base-modal-close="cancelModal"
    >
      <!-- header  -->
      <template v-slot:header>
        <div class="m-title">{{ getMessageText(messages.DISP_EL_TREND) }}</div>
      </template>

      <element-description
        v-if="isDescriptionReady"
        :label="descriptionText"
        :outer-container-class="'m-items'"
        :elem-description="desc"
        :value-container-class="'mt8 obj-select'"
        :focus="true"
        @on-element-description-changed="onDeDescriptionChanged"
      ></element-description>

      <div class="m-items">
        <div class="label-wrapper">
          <div class="label">{{ getMessageText(messages.DATA_SOURCE) }}:</div>
        </div>
        <div class="expression">
          <search-box-simple
            class="input-placeholder"
            id="dataSource"
            :list="sourcesList"
            :value="selectedSource"
            @on-search-box-item-selected="onSourceSelected"
          />
        </div>
      </div>

      <div class="m-items">
        <div class="label-wrapper">
          <div class="label">
            {{ getMessageText(messages.TREND_DATE_LABEL) }}:
          </div>
        </div>
        <div class="expression">
          <search-box-simple class="input-placeholder" v-if="!selectedSource" />
          <expressions
            v-else
            id="dateExpression"
            :data-elements="allSources"
            :selected-source="selectedSource"
            :data-type="'date'"
            :initial-expression="dateExpressionValue"
            :content-type="appContent.type"
            @on-expression-field-valid="onConfigExpressionValid"
          />
        </div>
      </div>

      <div class="m-items">
        <div class="label-wrapper">
          <div class="label">
            {{ getMessageText(messages.TREND_VALUE_LABEL) }}:
          </div>
        </div>
        <div class="expression">
          <search-box-simple class="input-placeholder" v-if="!selectedSource" />
          <expressions
            v-else
            id="valueExpression"
            :data-elements="allSources"
            :selected-source="selectedSource"
            :data-type="'number'"
            :initial-expression="valueExpressionValue"
            :content-type="appContent.type"
            @on-expression-field-valid="onConfigExpressionValid"
          />
        </div>
      </div>

      <div class="series-checkbox checkbox-wrapper checkbox-content">
        <label class="no-margin cursor-pointer" @click="toggleSeries">{{
          $t("trendChartIncludeSeries")
        }}</label>
        <b-form-checkbox v-model="isSeriesVisible"></b-form-checkbox>
      </div>

      <div class="m-items" v-if="isSeriesVisible">
        <div class="label-wrapper">
          <div class="label">
            {{ getMessageText(messages.TREND_SERIES_LABEL) }}:
          </div>
        </div>
        <div class="expression">
          <search-box-simple class="input-placeholder" v-if="!selectedSource" />
          <expressions
            v-else
            id="seriesExpression"
            :data-elements="allSources"
            :selected-source="selectedSource"
            :data-type="'string'"
            :initial-expression="seriesExpressionValue"
            :content-type="appContent.type"
            @on-expression-field-valid="onConfigExpressionValid"
          />
        </div>
      </div>

      <div class="m-items">
        <div class="label-wrapper">
          <div class="label">
            {{ getMessageText(messages.TREND_DATE_UNIT_LABEL) }}:
          </div>
        </div>
        <div class="expression">
          <search-box-simple
            class="input-placeholder"
            id="timeUnit"
            :has-icon="true"
            :list="unitOptions"
            :value="timeUnitValue"
            @on-search-box-item-selected="onTimeUnitSelected"
          />
        </div>
      </div>

      <!-- footer  -->
      <template v-slot:footer>
        <div class="m-group-btns">
          <button class="m-button neutral" v-on:click="cancelModal">
            {{ getMessageText(messages.CANCEL) }}
          </button>
          <button
            class="m-button brand"
            @click="createBtn"
            :disabled="isSubmitDisabled"
          >
            {{ getMessageText(messages.OK) }}
          </button>
        </div>
      </template>
    </base-modal>
  </section>
</template>

<script lang="js">
  import store from "@/store";
  import dateUtil from "@/utils/date-util";
  import expressionBuilder from "@/views/drag-drop-pages/components/utils/expression-builder";
  import dragAndDrop from "@/views/drag-drop-pages/components/utils/dragAndDrop";
  import BaseModal from "@/views/drag-drop-pages/components/modal/BaseModal";
  import SearchBoxSimple from "@/components/SearchBoxSimple";
  import Expressions from "@/components/Expressions"
  import ElementDescription from "@/components/ElementDescription";

  export default  {
    name: "add-trend-chart-modal",
    components: {
      BaseModal,
      SearchBoxSimple,
      Expressions,
      ElementDescription
    },
    props: {
      id: {
        type: String
      },
      editData: {
        type: Object
      }
    },
    mounted () {
      if(this.editData) {
        this.setupEditModalData(this.editData);
      }
      this.isDescriptionReady = true;
    },
    computed: {
      messages() {
        return store.state.messages;
      },
      isSeriesInputValid() {
        if (this.isSeriesVisible) {
          return !!this.seriesExpressionValue;
        }
        return true;
      },
      isSubmitDisabled() {
        return !this.selectedSource || !this.dateExpressionValue ||
               !this.valueExpressionValue ||
               !this.isSeriesInputValid || !this.timeUnitValue;
      },
      appId() {
        return dragAndDrop.getAppId();
      },
      appContent() {
        return JSON.parse(JSON.stringify(expressionBuilder.getAppContent(this.appId)));
      },
      allSources() {
        let output = null;
        if (this.appContent) {
          output = this.appContent.dataElements || {};
        } else {
          console.error(`No app content found for appId ${this.appId}`);
        }
        return output;
      },
      sourcesList() {
        return expressionBuilder.filterGlobalSources(this.allSources);
      },
      desc() {
        return this.editData ? this.editData.props.description : "";
      },
      descriptionText() {
        return this.getMessageText(this.messages.DESCRIPTION);
      }
    },
    data () {
      return {
        modalHeight: 500,
        modalWidth: 700,
        isGlobal: true,
        selectedSource: null,
        dateExpressionValue: null,
        valueExpressionValue: null,
        isSeriesVisible: false,
        seriesExpressionValue: null,
        timeUnitValue: null,
        unitOptions: dateUtil.getUnitOptions(),
        elementDescription: null,
        isDescriptionReady: false
      }
    },
    methods: {
      onSourceSelected(ctx) {
        if ( !ctx || ctx.isEmpty) {
          this.selectedSource = null;
          this.dateExpressionValue = null;
          this.valueExpressionValue = null;
          this.seriesExpressionValue = null;
        } else {
          this.selectedSource = ctx;
        }
      },
      onSourceCleared() {
        this.selectedSource = null;
        this.dateExpressionValue = null;
        this.valueExpressionValue = null;
        this.seriesExpressionValue = null;
      },
      onTimeUnitSelected(ctx) {
        if ( !ctx || ctx.isEmpty) {
          this.timeUnitValue = null;
        } else {
          this.timeUnitValue = ctx;
        }
      },
      onConfigExpressionValid(ctx) {
        if(ctx.id === "dateExpression") {
          this.dateExpressionValue = ctx.status? ctx.confExpression : null;
        } else if(ctx.id === "valueExpression") {
          this.valueExpressionValue = ctx.status? ctx.confExpression : null;
        } else if(ctx.id === "seriesExpression") {
          this.seriesExpressionValue = ctx.status? ctx.confExpression : null;
        }
      },
      setupEditModalData(editData) {
        if(editData) {
          this.onDeDescriptionChanged(editData.props.description)
          this.selectedSource = editData.props.dataSource;
          this.dateExpressionValue = editData.props.date;
          this.valueExpressionValue = editData.props.value;
          this.seriesExpressionValue = editData.props.series;
          if (this.seriesExpressionValue) {
            this.isSeriesVisible = true
          }
          this.timeUnitValue = editData.props.timeUnit;
        }
      },
      cancelModal() {
        const EVENT = "on-add-modal-close";
        if(this.$listeners[EVENT] || this._events[EVENT]) {
          dragAndDrop.makeAllComponentsDraggable();
          this.$emit(EVENT, {id: this.id});
        }
      },
      createBtn() {
        const obj = {
          modal: {
            id: this.id,
            create: "TrendChart",
            editElementId: this.editData? this.editData.id: null
          },
          props: {
            description: this.elementDescription,
            data: {
              dataSource: this.selectedSource,
              date: this.dateExpressionValue,
              value: this.valueExpressionValue,
              series: this.isSeriesVisible ? this.seriesExpressionValue : null,
              timeUnit: this.timeUnitValue
            }
          }
        };
        const EVENT = "on-add-modal-done";
        if(this.$listeners[EVENT] || this._events[EVENT]) {
          dragAndDrop.makeAllComponentsDraggable();
          this.$emit(EVENT, obj);
        }
      },
      toggleSeries() {
        this.isSeriesVisible = !this.isSeriesVisible;
      },
      onDeDescriptionChanged(input) {
        this.elementDescription = input;
      }
    }
  }
</script>

<style scoped lang="scss">
  .m-items {
    grid-template-columns: 6rem auto;
    margin-bottom: 10px;
    color: var(--DEFAULT_TEXT_BLACK);
    .label {
      overflow-wrap: anywhere;
      padding-top: 0.2rem;
    }
    .label-wrapper {
      padding-right: 1rem;
    }
    .expression {
      > .config-field {
        margin-top: 0;
      }
    }
    .expression,
    .label {
      display: flex;
      align-items: center;
      min-height: 2.5rem;
    }
  }
  .m-title {
    text-align: left;
  }
  .input-placeholder {
    width: 100%;
  }
  .series-checkbox {
    color: var(--DEFAULT_TEXT_BLACK);
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
</style>
